import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { database } from '../../firebase';
import { ref, get, set, update, push, onValue, off, increment } from 'firebase/database';
import { useAuth } from '../../context/AuthContext';
import {
  ArrowRightIcon, XMarkIcon, ClockIcon, UserIcon,
  ChevronLeftIcon, ChevronRightIcon, TagIcon,
  HeartIcon, ChatBubbleLeftEllipsisIcon, ShareIcon,
  LinkIcon, FolderIcon, SparklesIcon,
  PaperAirplaneIcon, TrashIcon, CalendarDaysIcon,
  FilmIcon, PhotoIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import logo from '../../assets/images/logo.jpeg';

// ─── IMPORT HERO IMAGE ────────────────────────────────────────────────────────
import fitneseqp12 from '../../assets/images/fitneseqp12.png';

// ─── FONTS ───────────────────────────────────────────────────────────────────
const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');`;

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay },
  viewport: { once: true },
});

const formatDate = (ts) =>
  ts ? new Date(ts).toLocaleDateString('en-KE', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

const formatRelative = (ts) => {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)  return `${d}d ago`;
  return formatDate(ts);
};

const getEmbedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
};

const buildShareUrl = (blog) => {
  const base = window.location.origin + window.location.pathname;
  return `${base}#${blog.slug || blog.id}`;
};

// ─── IMAGE SLIDER ─────────────────────────────────────────────────────────────
const ImageSlider = ({ photos, height = 220, rounded = 'rounded-2xl' }) => {
  const [idx, setIdx] = useState(0);
  if (!photos?.length) return null;
  const prev = (e) => { e?.stopPropagation(); setIdx(i => (i - 1 + photos.length) % photos.length); };
  const next = (e) => { e?.stopPropagation(); setIdx(i => (i + 1) % photos.length); };

  return (
    <div className={`relative w-full overflow-hidden ${rounded}`} style={{ height }}>
      <AnimatePresence mode="wait">
        <motion.img
          key={idx}
          src={photos[idx]}
          alt=""
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>

      {/* Gradient bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
        style={{ background: 'linear-gradient(to top,rgba(0,0,0,0.35),transparent)' }} />

      {photos.length > 1 && (
        <>
          <button onClick={prev}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/60 text-white backdrop-blur-sm transition-all z-10">
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <button onClick={next}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/60 text-white backdrop-blur-sm transition-all z-10">
            <ChevronRightIcon className="h-4 w-4" />
          </button>
          {/* Maroon indicator dots */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {photos.map((_, i) => (
              <button key={i} onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                className="transition-all rounded-full"
                style={{
                  width:  i === idx ? 20 : 7,
                  height: 7,
                  background: i === idx ? '#800020' : 'rgba(255,255,255,0.55)',
                }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─── SHARE POPUP ─────────────────────────────────────────────────────────────
const SharePopup = ({ blog, onClose }) => {
  const url     = buildShareUrl(blog);
  const text    = encodeURIComponent(`Check out this post: ${blog.title}`);
  const encUrl  = encodeURIComponent(url);
  const [copied, setCopied] = useState(false);

  const copyUrl = () => {
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const shares = [
    {
      label: 'WhatsApp',
      color: '#25D366',
      icon: (
        <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
          <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.555 4.116 1.528 5.845L.057 23.927l6.265-1.643A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-5.006-1.368l-.36-.214-3.719.976 1.002-3.624-.235-.373A9.818 9.818 0 012.182 12c0-5.42 4.398-9.818 9.818-9.818 5.42 0 9.818 4.398 9.818 9.818 0 5.42-4.398 9.818-9.818 9.818z"/>
        </svg>
      ),
      href: `https://wa.me/?text=${text}%20${encUrl}`,
    },
    {
      label: 'Facebook',
      color: '#1877F2',
      icon: (
        <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
          <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.268h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
        </svg>
      ),
      href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`,
    },
    {
      label: 'TikTok',
      color: '#000000',
      icon: (
        <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.74a4.85 4.85 0 01-1.01-.05z"/>
        </svg>
      ),
      href: `https://www.tiktok.com/share?url=${encUrl}`,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 8 }}
      transition={{ type: 'spring', damping: 20, stiffness: 300 }}
      className="absolute right-0 bottom-10 z-50 bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 w-56"
      onClick={e => e.stopPropagation()}>
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Share this post</p>
      <div className="space-y-2">
        {shares.map(s => (
          <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer"
            onClick={onClose}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-all text-sm font-medium text-gray-700 group">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white flex-shrink-0"
              style={{ background: s.color }}>
              {s.icon}
            </span>
            {s.label}
          </a>
        ))}
        <button onClick={copyUrl}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-all text-sm font-medium text-gray-700">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center text-white flex-shrink-0"
            style={{ background: copied ? '#16a34a' : '#374151' }}>
            <LinkIcon className="h-4 w-4" />
          </span>
          {copied ? 'Copied!' : 'Copy Link'}
        </button>
      </div>
    </motion.div>
  );
};

// ─── BLOG CARD ────────────────────────────────────────────────────────────────
const BlogCard = ({ blog, categories, onOpen, likesData, onLike, currentUser }) => {
  const [shareOpen, setShareOpen] = useState(false);
  const shareRef = useRef(null);
  const cat = categories.find(c => c.id === blog.categoryId);
  const allPhotos = [blog.coverImage, ...(blog.photos || [])].filter(Boolean);
  const likes = likesData?.[blog.id]?.count || 0;
  const liked = currentUser && likesData?.[blog.id]?.users?.[currentUser.uid];

  useEffect(() => {
    const handler = (e) => { if (shareRef.current && !shareRef.current.contains(e.target)) setShareOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="group bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 flex flex-col">

      {/* Image slider */}
      {allPhotos.length > 0 ? (
        <div className="cursor-pointer" onClick={() => onOpen(blog)}>
          <ImageSlider photos={allPhotos} height={220} rounded="rounded-none" />
        </div>
      ) : (
        <div onClick={() => onOpen(blog)}
          className="cursor-pointer h-48 flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg,#1a1a2e,#800020)' }}>
          <SparklesIcon className="h-10 w-10 text-white/20" />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 flex flex-col p-5">
        {/* Meta */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {cat && (
            <span className="text-xs font-bold px-2.5 py-1 rounded-full"
              style={{ background: (cat.color || '#800020') + '18', color: cat.color || '#800020' }}>
              {cat.name}
            </span>
          )}
          {blog.featured && (
            <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: '#fef2f2', color: '#800020' }}>
              ⭐ Featured
            </span>
          )}
          <span className="text-xs text-gray-400 flex items-center gap-1 ml-auto">
            <ClockIcon className="h-3 w-3" />
            {blog.readTime || 1} min read
          </span>
        </div>

        {/* Title */}
        <h3 onClick={() => onOpen(blog)}
          className="font-black text-gray-900 mb-2 leading-tight cursor-pointer group-hover:text-red-900 transition-colors line-clamp-2"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.25rem' }}>
          {blog.title}
        </h3>

        {/* Excerpt */}
        {blog.excerpt && (
          <p className="text-sm text-gray-500 leading-relaxed line-clamp-2 mb-3 flex-1">{blog.excerpt}</p>
        )}

        {/* Tags */}
        {blog.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {blog.tags.slice(0, 3).map((t, i) => (
              <span key={i} className="text-xs text-gray-400 flex items-center gap-0.5">
                <TagIcon className="h-2.5 w-2.5" />#{t}
              </span>
            ))}
          </div>
        )}

        {/* Author + date */}
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
          {blog.author && (
            <span className="flex items-center gap-1"><UserIcon className="h-3 w-3" />{blog.author}</span>
          )}
          {blog.author && blog.createdAt && <span>·</span>}
          {blog.createdAt && (
            <span className="flex items-center gap-1"><CalendarDaysIcon className="h-3 w-3" />{formatDate(blog.publishedAt || blog.createdAt)}</span>
          )}
        </div>

        {/* Footer row */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          {/* Read more */}
          <button onClick={() => onOpen(blog)}
            className="flex items-center gap-1 text-sm font-bold transition-all group-hover:gap-2"
            style={{ color: '#800020' }}>
            Read More <ArrowRightIcon className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
          </button>

          {/* Like + Share */}
          <div className="flex items-center gap-3">
            <button onClick={() => onLike(blog.id)}
              className="flex items-center gap-1.5 text-xs font-medium transition-all"
              style={{ color: liked ? '#800020' : '#9ca3af' }}>
              {liked
                ? <HeartSolid className="h-4 w-4" style={{ color: '#800020' }} />
                : <HeartIcon className="h-4 w-4" />
              }
              {likes > 0 && <span>{likes}</span>}
            </button>

            <div className="relative" ref={shareRef}>
              <button onClick={() => setShareOpen(o => !o)}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-all">
                <ShareIcon className="h-4 w-4" />
              </button>
              <AnimatePresence>
                {shareOpen && <SharePopup blog={blog} onClose={() => setShareOpen(false)} />}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ─── COMMENT INPUT ────────────────────────────────────────────────────────────
const CommentInput = ({ blogId, currentUser, userData, onPosted }) => {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || !currentUser) return;
    setLoading(true);
    try {
      const nr = push(ref(database, `blogComments/${blogId}`));
      await set(nr, {
        id:        nr.key,
        text:      text.trim(),
        userId:    currentUser.uid,
        userName:  userData?.personalInfo?.name || currentUser.email?.split('@')[0] || 'Member',
        userEmail: currentUser.email || '',
        createdAt: Date.now(),
      });
      setText('');
      if (onPosted) onPosted();
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (!currentUser) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 p-5 text-center">
        <ChatBubbleLeftEllipsisIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500 mb-2">Sign in to leave a comment</p>
        <Link to="/login"
          className="inline-flex items-center gap-1 text-sm font-bold px-4 py-2 rounded-full text-white transition-all"
          style={{ background: '#800020' }}>
          Sign In <ArrowRightIcon className="h-3.5 w-3.5" />
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex gap-3 items-end">
      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
        style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
        {(userData?.personalInfo?.name || currentUser.email || 'U')[0].toUpperCase()}
      </div>
      <div className="flex-1 relative">
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(e); } }}
          rows={2}
          placeholder="Share your thoughts… (Enter to post)"
          className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-2xl text-sm resize-none focus:outline-none focus:ring-2 transition-all"
          style={{ '--tw-ring-color': '#800020' }}
        />
        <button type="submit" disabled={!text.trim() || loading}
          className="absolute right-3 bottom-3 w-7 h-7 flex items-center justify-center rounded-full text-white transition-all disabled:opacity-40"
          style={{ background: '#800020' }}>
          {loading
            ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <PaperAirplaneIcon className="h-3.5 w-3.5" />
          }
        </button>
      </div>
    </form>
  );
};

// ─── BLOG DETAIL MODAL ────────────────────────────────────────────────────────
const BlogDetailModal = ({ blog, categories, onClose, likesData, onLike, currentUser, userData }) => {
  const [comments,     setComments]     = useState([]);
  const [loadingCmts,  setLoadingCmts]  = useState(true);
  const [shareOpen,    setShareOpen]    = useState(false);
  const shareRef = useRef(null);

  const cat    = categories.find(c => c.id === blog.categoryId);
  const allPhotos = [blog.coverImage, ...(blog.photos || [])].filter(Boolean);
  const likes  = likesData?.[blog.id]?.count || 0;
  const liked  = currentUser && likesData?.[blog.id]?.users?.[currentUser.uid];
  const embedUrl = getEmbedUrl(blog.videoUrl || blog.videoFile);

  // Load comments realtime
  useEffect(() => {
    const cmtRef = ref(database, `blogComments/${blog.id}`);
    setLoadingCmts(true);
    const unsub = onValue(cmtRef, snap => {
      setComments(snap.exists()
        ? Object.values(snap.val()).sort((a, b) => b.createdAt - a.createdAt)
        : []);
      setLoadingCmts(false);
    });
    return () => off(cmtRef, 'value', unsub);
  }, [blog.id]);

  // Update URL hash for deep linking
  useEffect(() => {
    window.history.replaceState(null, '', `${window.location.pathname}#${blog.slug || blog.id}`);
    return () => window.history.replaceState(null, '', window.location.pathname);
  }, [blog.slug, blog.id]);

  useEffect(() => {
    const handler = (e) => { if (shareRef.current && !shareRef.current.contains(e.target)) setShareOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const deleteComment = async (commentId) => {
    await set(ref(database, `blogComments/${blog.id}/${commentId}`), null);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', fontFamily: "'DM Sans', sans-serif" }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 32, stiffness: 300 }}
        className="relative bg-white w-full flex flex-col"
        style={{ marginTop: '3vh', borderRadius: '24px 24px 0 0', height: '97vh' }}
        onClick={e => e.stopPropagation()}>

        {/* Top drag handle */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">

          {/* ── IMAGE SECTION ── */}
          {allPhotos.length > 0 && (
            <div className="px-4 pt-2">
              <ImageSlider photos={allPhotos} height={280} rounded="rounded-2xl" />
            </div>
          )}

          <div className="px-5 md:px-8 py-6 max-w-3xl mx-auto">

            {/* Category + meta */}
            <div className="flex items-center gap-2 flex-wrap mb-3">
              {cat && (
                <span className="text-xs font-bold px-2.5 py-1 rounded-full"
                  style={{ background: (cat.color || '#800020') + '18', color: cat.color || '#800020' }}>
                  {cat.name}
                </span>
              )}
              {blog.featured && (
                <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: '#fef2f2', color: '#800020' }}>
                  ⭐ Featured
                </span>
              )}
              <span className="text-xs text-gray-400 flex items-center gap-1 ml-auto">
                <ClockIcon className="h-3 w-3" />{blog.readTime || 1} min read
              </span>
            </div>

            {/* Title */}
            <h1 className="font-black text-gray-900 leading-tight mb-3"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(1.8rem,5vw,2.8rem)' }}>
              {blog.title}
            </h1>

            {/* Author + date */}
            <div className="flex items-center gap-3 mb-5 pb-5 border-b border-gray-100">
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
                {(blog.author || 'P')[0].toUpperCase()}
              </div>
              <div>
                {blog.author && <p className="text-sm font-semibold text-gray-800">{blog.author}</p>}
                <p className="text-xs text-gray-400">{formatDate(blog.publishedAt || blog.createdAt)}</p>
              </div>

              {/* Like + Share in header */}
              <div className="ml-auto flex items-center gap-3">
                <button onClick={() => onLike(blog.id)}
                  className="flex items-center gap-1.5 text-sm font-semibold transition-all px-3 py-1.5 rounded-full"
                  style={{
                    background: liked ? '#fef2f2' : '#f9fafb',
                    color: liked ? '#800020' : '#6b7280',
                  }}>
                  {liked ? <HeartSolid className="h-4 w-4" style={{ color: '#800020' }} /> : <HeartIcon className="h-4 w-4" />}
                  {likes > 0 ? likes : 'Like'}
                </button>
                <div className="relative" ref={shareRef}>
                  <button onClick={() => setShareOpen(o => !o)}
                    className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-all">
                    <ShareIcon className="h-4 w-4" /> Share
                  </button>
                  <AnimatePresence>
                    {shareOpen && <SharePopup blog={blog} onClose={() => setShareOpen(false)} />}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            {/* Excerpt */}
            {blog.excerpt && (
              <p className="text-base text-gray-600 leading-relaxed mb-6 font-medium italic border-l-4 pl-4"
                style={{ borderColor: '#800020' }}>
                {blog.excerpt}
              </p>
            )}

            {/* ── CONTENT SECTIONS ── */}
            {(blog.description || []).map((sec, i) => (
              <div key={i} className="mb-6">
                {sec.title && (
                  <h2 className="font-black text-gray-900 mb-2"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.4rem' }}>
                    {sec.title}
                  </h2>
                )}
                {sec.content && (
                  <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{sec.content}</p>
                )}
              </div>
            ))}

            {/* ── VIDEO ── */}
            {embedUrl && (
              <div className="mb-8 rounded-2xl overflow-hidden bg-black" style={{ aspectRatio: '16/9' }}>
                {blog.videoUrl ? (
                  <iframe src={embedUrl} title="Video" className="w-full h-full" allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
                ) : (
                  <video src={embedUrl} controls className="w-full h-full" />
                )}
              </div>
            )}

            {/* ── TAGS ── */}
            {blog.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8 pt-4 border-t border-gray-100">
                {blog.tags.map((t, i) => (
                  <span key={i} className="text-xs px-3 py-1.5 rounded-full font-medium border"
                    style={{ background: '#fef2f2', color: '#800020', borderColor: '#fcd5de' }}>
                    #{t}
                  </span>
                ))}
              </div>
            )}

            {/* ── COMMENTS ── */}
            <div className="pt-4 border-t border-gray-100">
              <h3 className="font-black text-gray-900 mb-5"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.3rem' }}>
                {comments.length > 0 ? `${comments.length} Comment${comments.length !== 1 ? 's' : ''}` : 'Comments'}
              </h3>

              {/* Comment input */}
              <div className="mb-6">
                <CommentInput
                  blogId={blog.id}
                  currentUser={currentUser}
                  userData={userData}
                />
              </div>

              {/* Comments list */}
              {loadingCmts ? (
                <div className="text-center py-8">
                  <div className="inline-block w-6 h-6 border-2 border-red-200 border-t-red-700 rounded-full animate-spin" />
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-10 rounded-2xl bg-gray-50">
                  <ChatBubbleLeftEllipsisIcon className="h-10 w-10 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">No comments yet. Be the first to share your thoughts!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {comments.map(cmt => {
                    const isOwn = currentUser && (currentUser.uid === cmt.userId);
                    return (
                      <motion.div key={cmt.id}
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                        className="flex gap-3 group">
                        {/* Avatar */}
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-0.5"
                          style={{ background: 'linear-gradient(135deg,#1a1a2e,#800020)' }}>
                          {(cmt.userName || 'U')[0].toUpperCase()}
                        </div>
                        {/* Bubble */}
                        <div className="flex-1 min-w-0">
                          <div className="bg-gray-50 rounded-2xl rounded-tl-sm px-4 py-3">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <p className="text-xs font-bold text-gray-800">{cmt.userName}</p>
                              <p className="text-xs text-gray-400 flex-shrink-0">{formatRelative(cmt.createdAt)}</p>
                            </div>
                            <p className="text-sm text-gray-600 leading-relaxed">{cmt.text}</p>
                          </div>
                          {isOwn && (
                            <button onClick={() => deleteComment(cmt.id)}
                              className="mt-1 ml-3 text-xs text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 flex items-center gap-1">
                              <TrashIcon className="h-3 w-3" /> Delete
                            </button>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Bottom padding */}
            <div className="h-8" />
          </div>
        </div>

        {/* Close button */}
        <button onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-all z-10">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const Blog = () => {
  const { currentUser, userData } = useAuth();
  const location = useLocation();

  const [blogs,       setBlogs]       = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selected,    setSelected]    = useState(null);
  const [likesData,   setLikesData]   = useState({});
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery,    setSearchQuery]    = useState('');

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [bSnap, cSnap] = await Promise.all([
          get(ref(database, 'blogs')),
          get(ref(database, 'blogCategories')),
        ]);
        const blogList = bSnap.exists()
          ? Object.entries(bSnap.val())
              .map(([k, v]) => ({ id: k, ...v }))
              .filter(b => b.status === 'published')
              .sort((a, b) => (b.publishedAt || b.createdAt) - (a.publishedAt || a.createdAt))
          : [];
        setBlogs(blogList);
        setCategories(cSnap.exists()
          ? Object.entries(cSnap.val()).map(([k, v]) => ({ id: k, ...v }))
          : []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  // ── Likes realtime ─────────────────────────────────────────────────────────
  useEffect(() => {
    const likesRef = ref(database, 'blogLikes');
    const unsub = onValue(likesRef, snap => {
      setLikesData(snap.exists() ? snap.val() : {});
    });
    return () => off(likesRef, 'value', unsub);
  }, []);

  // ── Deep-link via hash ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!blogs.length) return;
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const found = blogs.find(b => b.slug === hash || b.id === hash);
    if (found) setSelected(found);
  }, [blogs]);

  // ── Like handler ───────────────────────────────────────────────────────────
  const handleLike = async (blogId) => {
    const uid = currentUser?.uid || `guest_${Math.random().toString(36).slice(2)}`;
    const alreadyLiked = currentUser && likesData?.[blogId]?.users?.[currentUser.uid];
    const likesRef = ref(database, `blogLikes/${blogId}`);

    if (!currentUser) {
      // Guest: just increment count, no undo
      await update(likesRef, { count: increment(1) });
      return;
    }
    if (alreadyLiked) {
      // Unlike
      await update(ref(database, `blogLikes/${blogId}/users`), { [currentUser.uid]: null });
      await update(likesRef, { count: increment(-1) });
    } else {
      // Like
      await update(ref(database, `blogLikes/${blogId}/users`), { [currentUser.uid]: true });
      await update(likesRef, { count: increment(1) });
    }
  };

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filtered = blogs.filter(b => {
    const q = searchQuery.toLowerCase();
    const matchCat    = activeCategory === 'all' || b.categoryId === activeCategory;
    const matchSearch = !q || b.title?.toLowerCase().includes(q) || b.excerpt?.toLowerCase().includes(q) || b.tags?.some(t => t.toLowerCase().includes(q));
    return matchCat && matchSearch;
  });

  const featured = blogs.filter(b => b.featured);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{FONTS}</style>

      {/* ══════════════════════════════════════════════════════
          1. HERO — using fitneseqp12.png with no overflow color
      ══════════════════════════════════════════════════════ */}
      <section className="relative w-full overflow-hidden" style={{ height: '500px' }}>
        <img src={fitneseqp12} alt="PowerCore Blog" className="absolute inset-0 w-full h-full object-cover object-center" />

        {/* Clean dark overlay — no maroon tint, just dark gradient */}
        <div className="absolute inset-0"
          style={{ background: 'linear-gradient(120deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 60%, rgba(0,0,0,0.3) 100%)' }} />

        {/* Subtle grid texture */}
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.1) 1px,transparent 1px)', backgroundSize: '40px 40px' }} />

        <div className="absolute inset-0 flex flex-col justify-center">
          <div className="container-custom max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fade(0.1)} className="max-w-2xl">
              <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase px-3 py-1 rounded-full mb-4 border border-white/30 text-white/70">
                PowerCore Journal
              </span>
              <h1 className="font-black text-white mb-4 leading-none"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(2.5rem,6vw,4.5rem)', letterSpacing: '-0.02em' }}>
                STORIES THAT<br />
                <span style={{ color: '#e8a0b0' }}>MOVE YOU.</span>
              </h1>
              <p className="text-white/70 text-sm leading-relaxed max-w-lg">
                Expert insights, training tips, nutrition guides and community stories — all from the heart of PowerCore.
              </p>
            </motion.div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-16"
          style={{ background: 'linear-gradient(to top, white, transparent)' }} />
      </section>

      {/* ══════════════════════════════════════════════════════
          2. FEATURED (if any)
      ══════════════════════════════════════════════════════ */}
      {featured.length > 0 && !loading && (
        <section className="py-12 bg-white">
          <div className="container-custom max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div {...fade(0)} className="flex items-center gap-3 mb-6">
              <span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: '#800020' }}>Featured</span>
              <div className="flex-1 h-px bg-gray-100" />
            </motion.div>
            <div className="grid lg:grid-cols-2 gap-6">
              {featured.slice(0, 2).map(blog => (
                <BlogCard
                  key={blog.id}
                  blog={blog}
                  categories={categories}
                  onOpen={setSelected}
                  likesData={likesData}
                  onLike={handleLike}
                  currentUser={currentUser}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════════════════════════════════════════════════
          3. FILTER BAR
      ══════════════════════════════════════════════════════ */}
      <section className="py-8 sticky top-0 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="container-custom max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            {/* Search */}
            <div className="relative flex-1 max-w-xs">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search posts…"
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 bg-white"
                style={{ '--tw-ring-color': '#800020' }} />
            </div>

            {/* Category pills */}
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setActiveCategory('all')}
                className="px-4 py-2 rounded-full text-xs font-bold transition-all"
                style={{
                  background:   activeCategory === 'all' ? '#800020' : '#f3f4f6',
                  color:        activeCategory === 'all' ? '#fff'    : '#6b7280',
                }}>
                All
              </button>
              {categories.map(cat => (
                <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
                  className="px-4 py-2 rounded-full text-xs font-bold transition-all"
                  style={{
                    background:   activeCategory === cat.id ? (cat.color || '#800020') : '#f3f4f6',
                    color:        activeCategory === cat.id ? '#fff'                   : '#6b7280',
                  }}>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          4. BLOG GRID
      ══════════════════════════════════════════════════════ */}
      <section className="py-14" style={{ background: '#faf8f8' }}>
        <div className="container-custom max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-24">
              <div className="inline-block w-10 h-10 border-4 border-red-200 border-t-red-700 rounded-full animate-spin" />
              <p className="text-gray-400 mt-3 text-sm">Loading posts…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24">
              <SparklesIcon className="h-14 w-14 text-gray-200 mx-auto mb-4" />
              <p className="text-gray-400 font-medium">No posts found</p>
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="mt-2 text-sm font-semibold" style={{ color: '#800020' }}>
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="text-xs text-gray-400 font-medium mb-6">{filtered.length} post{filtered.length !== 1 ? 's' : ''}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map(blog => (
                  <BlogCard
                    key={blog.id}
                    blog={blog}
                    categories={categories}
                    onOpen={setSelected}
                    likesData={likesData}
                    onLike={handleLike}
                    currentUser={currentUser}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
          5. READY TO START — same as About
      ══════════════════════════════════════════════════════ */}
      <section className="py-14 relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#800020 0%,#3d0010 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl" />
        </div>
        <div className="container-custom max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div {...fade(0)}>
            <span className="text-xs font-bold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full inline-block mb-4 bg-white/20 text-white/80">
              Limited Spots Available
            </span>
            <h2 className="font-black text-white mb-4 leading-none"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(2.5rem,5vw,4rem)' }}>
              READY TO START YOUR <span className="text-yellow-400">JOURNEY?</span>
            </h2>
            <p className="text-white/75 mb-8 max-w-lg mx-auto text-sm leading-relaxed">
              Join PowerCore today and get <strong className="text-white">7 days free training</strong> with any monthly membership. No contracts. No excuses. Just results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing"
                className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-black text-sm text-gray-900 bg-white transition-all hover:scale-105 hover:shadow-2xl">
                View Pricing &amp; Join <ArrowRightIcon className="h-4 w-4" />
              </Link>
              <Link to="/facility"
                className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full font-bold text-sm text-white border-2 border-white/40 hover:bg-white/10 transition-all">
                Tour the Facility
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── BLOG DETAIL MODAL ── */}
      <AnimatePresence>
        {selected && (
          <BlogDetailModal
            blog={selected}
            categories={categories}
            onClose={() => setSelected(null)}
            likesData={likesData}
            onLike={handleLike}
            currentUser={currentUser}
            userData={userData}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Blog;